import React, { useEffect, useState } from "react";
import { all_routes } from "../../../router/all_routes";
import Breadcrumb from "../../../../core/common/Breadcrumb/breadcrumb";
import Sidebar from "../../../../core/common/sidebar/sidebar";
import BookingCard from "./BookingCard";
import axios from "axios";

interface Compra {
  compraId: number;
  status: string;
  dataReserva: string;
  precoTotal: number;
  adultos: number;
  criancas: number;
  motoristaNome: string;
  pacote: {
    id: number;
    titulo: string;
    duracaoEmMinutos: number;
    primeiraFotoId?: number;
  };
  qrCodeUsuario?: string;
  unreadCount?: number;
}

interface CompraUnreadDTO {
  compraId: number;
  naoVisualizadas: number;
}

interface AvaliacaoUsuario {
  nota: number;
  comentario: string;
}

const UserTukTukBooking = () => {
  const routes = all_routes;
  const [compras, setCompras] = useState<Compra[]>([]);
  const [avaliacoes, setAvaliacoes] = useState<Map<number, AvaliacaoUsuario>>(new Map());
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  const BASE_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const breadcrumbs = [
    { label: "My Bookings", link: routes.userDashboard, active: false },
    { label: "Tuk Tuk", active: true },
  ];

  useEffect(() => {
    if (!token) return;

    const fetchCompras = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/pacotes/compras/minhas`, { headers });
        const comprasData: Compra[] = res.data;

        const unreadRes = await axios.get<CompraUnreadDTO[]>(
          `${BASE_URL}/usuario/mensagens/nao-visualizadas-por-compra`,
          { headers }
        );

        const unreadMap = new Map<number, number>();
        unreadRes.data.forEach(u => unreadMap.set(u.compraId, u.naoVisualizadas));

        // Mescla contagem e inicializa avaliação vazia
        const comprasComBadge = comprasData.map(compra => ({
          ...compra,
          unreadCount: unreadMap.get(compra.compraId) || 0
        }));

        setCompras(comprasComBadge);

        // Buscar avaliações do usuário para cada compra concluída
        const avaliacoesMap = new Map<number, AvaliacaoUsuario>();
        await Promise.all(
          comprasComBadge
            .filter(c => c.status.toUpperCase() === "CONCLUIDO")
            .map(async c => {
              try {
                const res = await axios.get(`${BASE_URL}/avaliacoes/usuario/${c.compraId}`, { headers });
                if (res.data) {
                  avaliacoesMap.set(c.compraId, {
                    nota: res.data.nota,
                    comentario: res.data.comentario
                  });
                }
              } catch {
                // caso não tenha avaliação, ignora
              }
            })
        );
        setAvaliacoes(avaliacoesMap);

      } catch (err) {
        console.error("Error fetching bookings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompras();
  }, [BASE_URL, token]);

  // Paginação
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = compras.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(compras.length / itemsPerPage);

  return (
    <div>
      <Breadcrumb
        title="Tuk Tuk Bookings"
        breadcrumbs={breadcrumbs}
        backgroundClass="breadcrumb-bg-01"
      />

      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4">
              <Sidebar />
            </div>

            <div className="col-xl-9 col-lg-8">
              <div className="card shadow-none mb-0">
                <div className="card-header">
                  <h6>My Tuk Tuk Bookings</h6>
                </div>

                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" />
                      <p className="mt-3">Loading bookings...</p>
                    </div>
                  ) : compras.length === 0 ? (
                    <div className="alert alert-info">You have no bookings yet.</div>
                  ) : (
                    <>
                      {currentItems.map(compra => (
                        <BookingCard key={compra.compraId} {...compra} avaliacao={avaliacoes.get(compra.compraId)}/>
                      ))}

                      <nav className="mt-4">
                        <ul className="pagination justify-content-center">
                          {[...Array(totalPages)].map((_, index) => (
                            <li
                              key={index}
                              className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTukTukBooking;
